/**
 * NotificationsAdminCard — channel matrix + template editor.
 *
 * Renders inside Admin Setup → Notifications tab. Super-admin flips per-event
 * channel toggles (email/sms/whatsapp) and edits the template body for each.
 */
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Smartphone, MessageCircle, Save, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const EVENT_LABELS: Record<string, { title: string; desc: string }> = {
  otp_signup:      { title: "OTP · Signup / Login",     desc: "6-digit verification code sent during signup." },
  order_shipped:   { title: "Order · Shipped",           desc: "Fires when an order is marked shipped." },
  order_delivered: { title: "Order · Delivered",         desc: "Fires when an order is marked delivered." },
  order_cancelled: { title: "Order · Cancelled",         desc: "Fires when an order is cancelled." },
  ticket_reply:    { title: "Support · Ticket Reply",    desc: "Fires when support replies to a customer ticket." },
};

const CHANNEL_ICON: Record<string, any> = { email: Mail, sms: Smartphone, whatsapp: MessageCircle };
const CHANNEL_HINT: Record<string, string> = {
  email: "Free · uses your SMTP",
  sms: "Costs money · Twilio",
  whatsapp: "Costs money · Twilio",
};


export const NotificationsAdminCard: React.FC = () => {
  const qc = useQueryClient();
  const cfg = useQuery<{ events: Record<string, Record<string, boolean>> }>({
    queryKey: ["notif-channels"],
    queryFn: async () => (await api.get("/admin/notifications/channels")).data,
  });
  const tpls = useQuery<{ items: any[]; events: string[]; tokens: string[] }>({
    queryKey: ["notif-templates"],
    queryFn: async () => (await api.get("/admin/notifications/templates")).data,
  });

  const [editing, setEditing] = React.useState<{ event: string; channel: string } | null>(null);

  const flip = useMutation({
    mutationFn: async ({ event, channel, on }: { event: string; channel: string; on: boolean }) =>
      (await api.put("/admin/notifications/channels", { events: { [event]: { [channel]: on } } })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["notif-channels"] }); },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Toggle failed"),
  });

  const events = cfg.data?.events || {};
  const tplMap: Record<string, any> = {};
  (tpls.data?.items || []).forEach((t) => { tplMap[`${t.event}:${t.channel}`] = t; });

  return (
    <div className="sg-card p-6 mt-6" data-testid="notifications-admin-card">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
        <div>
          <div className="sg-label">Notification channels</div>
          <p className="text-sm text-[color:var(--sg-muted)] max-w-2xl mt-0.5">
            Turn each channel ON/OFF per event. Email is free (uses your SMTP). SMS &amp; WhatsApp cost money via Twilio — leave OFF unless you need them.
          </p>
        </div>
      </div>

      {cfg.isLoading ? (
        <div className="py-10 text-center text-[color:var(--sg-muted)]"><Loader2 className="h-5 w-5 animate-spin inline mr-2"/>Loading…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="notif-channel-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)]">
                <th className="py-2 pr-3">Event</th>
                {(["email", "sms", "whatsapp"] as const).map((ch) => {
                  const Ico = CHANNEL_ICON[ch];
                  return (
                    <th key={ch} className="py-2 px-3 text-center">
                      <div className="inline-flex items-center gap-1">
                        <Ico className="h-3.5 w-3.5"/> {ch.toUpperCase()}
                      </div>
                      <div className="text-[9px] font-normal normal-case text-[color:var(--sg-muted)] mt-0.5">{CHANNEL_HINT[ch]}</div>
                    </th>
                  );
                })}
                <th className="py-2 pl-3 text-right">Templates</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--sg-border)]">
              {Object.entries(events).map(([ev, chans]) => {
                const meta = EVENT_LABELS[ev] || { title: ev, desc: "" };
                return (
                  <tr key={ev} data-testid={`notif-row-${ev}`}>
                    <td className="py-3 pr-3">
                      <div className="text-sm font-semibold text-[color:var(--sg-fg)]">{meta.title}</div>
                      <div className="text-[11px] text-[color:var(--sg-muted)]">{meta.desc}</div>
                    </td>
                    {(["email", "sms", "whatsapp"] as const).map((ch) => (
                      <td key={ch} className="py-3 px-3 text-center">
                        <Switch
                          data-testid={`notif-toggle-${ev}-${ch}`}
                          checked={!!chans?.[ch]}
                          onCheckedChange={(on) => flip.mutate({ event: ev, channel: ch, on })}
                        />
                      </td>
                    ))}
                    <td className="py-3 pl-3 text-right">
                      <div className="inline-flex gap-1">
                        {(["email", "sms", "whatsapp"] as const).map((ch) => (
                          <button
                            key={ch}
                            data-testid={`notif-edit-${ev}-${ch}`}
                            onClick={() => setEditing({ event: ev, channel: ch })}
                            className="text-[10px] px-2 py-0.5 rounded-full border border-[color:var(--sg-border)] hover:bg-[color:var(--sg-muted-bg)] uppercase font-semibold"
                          >{ch}</button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <TemplateEditor
          event={editing.event}
          channel={editing.channel}
          initial={tplMap[`${editing.event}:${editing.channel}`]}
          tokens={tpls.data?.tokens || []}
          onClose={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["notif-templates"] }); }}
        />
      )}
    </div>
  );
};


const TemplateEditor: React.FC<{
  event: string; channel: string; initial: any; tokens: string[]; onClose: () => void;
}> = ({ event, channel, initial, tokens, onClose }) => {
  const [subject, setSubject] = React.useState(initial?.subject || "");
  const [bodyHtml, setBodyHtml] = React.useState(initial?.body_html || "");
  const [bodyText, setBodyText] = React.useState(initial?.body_text || "");
  const [preview, setPreview] = React.useState<any>(null);

  const save = useMutation({
    mutationFn: async () => (await api.put(`/admin/notifications/templates/${event}/${channel}`, {
      subject: channel === "email" ? subject : undefined,
      body_html: channel === "email" ? bodyHtml : undefined,
      body_text: bodyText,
    })).data,
    onSuccess: () => { toast.success("Template saved."); onClose(); },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const previewMut = useMutation({
    mutationFn: async () => {
      const ctx: Record<string, string> = {};
      tokens.forEach((t) => { ctx[t] = `[${t}]`; });
      return (await api.post("/admin/notifications/templates/preview", { event, channel, context: ctx })).data;
    },
    onSuccess: (d) => setPreview(d),
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="tpl-editor-modal">
      <div className="bg-[color:var(--sg-card)] rounded-2xl border border-[color:var(--sg-border)] max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[color:var(--sg-border)] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[color:var(--sb-saffron,#F97316)]">Template · {channel}</div>
            <div className="text-lg font-serif-display font-bold text-[color:var(--sg-fg)]">{EVENT_LABELS[event]?.title || event}</div>
          </div>
          <button onClick={onClose} className="text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)] text-2xl leading-none">×</button>
        </div>
        <div className="p-5 space-y-3">
          {channel === "email" && (
            <div>
              <div className="sg-label mb-1">Subject</div>
              <input data-testid="tpl-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full h-10 rounded-md border border-[color:var(--sg-border)] bg-transparent px-3 text-sm"/>
            </div>
          )}
          {channel === "email" && (
            <div>
              <div className="sg-label mb-1">HTML body</div>
              <textarea data-testid="tpl-body-html" value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} rows={8} className="w-full rounded-md border border-[color:var(--sg-border)] bg-transparent px-3 py-2 text-xs font-mono"/>
            </div>
          )}
          <div>
            <div className="sg-label mb-1">{channel === "email" ? "Plain-text body" : "Message body"}</div>
            <textarea data-testid="tpl-body-text" value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={4} maxLength={1600} className="w-full rounded-md border border-[color:var(--sg-border)] bg-transparent px-3 py-2 text-sm font-mono"/>
            <div className="text-[10px] text-[color:var(--sg-muted)] mt-1">{bodyText.length} / 1600</div>
          </div>
          <div className="text-[11px] text-[color:var(--sg-muted)]">
            <strong>Available merge tags:</strong>{" "}
            {tokens.map((t) => <code key={t} className="ml-1 px-1.5 py-0.5 bg-[color:var(--sg-muted-bg)] rounded">{`{{${t}}}`}</code>)}
          </div>

          {preview && (
            <div className="mt-3 rounded-lg border border-dashed border-[color:var(--sb-saffron,#F97316)]/40 p-3 bg-[color:var(--sb-saffron,#F97316)]/5" data-testid="tpl-preview">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-[color:var(--sg-muted)] mb-1">Preview</div>
              {preview.subject && <div className="text-sm font-bold text-[color:var(--sg-fg)] mb-1">Subject: {preview.subject}</div>}
              {preview.body_html && channel === "email" ? (
                <div className="text-sm border-t border-[color:var(--sg-border)] pt-2 mt-1" dangerouslySetInnerHTML={{ __html: preview.body_html }}/>
              ) : (
                <pre className="text-xs whitespace-pre-wrap text-[color:var(--sg-fg)]">{preview.body_text}</pre>
              )}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-[color:var(--sg-border)] flex items-center justify-between">
          <Button data-testid="tpl-preview-btn" onClick={() => previewMut.mutate()} variant="outline" size="sm">
            <Eye className="h-3.5 w-3.5 mr-1.5"/> Preview
          </Button>
          <Button data-testid="tpl-save" onClick={() => save.mutate()} disabled={save.isPending} className="bg-[color:var(--sb-saffron,#F97316)] hover:bg-[color:var(--sb-saffron-hover,#EA580C)] text-white">
            {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5"/> : <Save className="h-3.5 w-3.5 mr-1.5"/>}
            Save template
          </Button>
        </div>
      </div>
    </div>
  );
};
